import React, { FC, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { anchoredPopoverStyle, useAnchoredPosition } from "../../../../utils/hooks/useAnchoredPosition";
import { useTranslate } from "../../../../utils/hooks/useTranslate";

import styles from "./styles.module.scss";

type MobileActionsMenuProps = {
  children: React.ReactNode;
};

// The ⋮ overflow menu the delivery page shows on mobile for a version's actions. spec-49 moved it to its own
// file so the versions library can reuse it.
//
// The dropdown is rendered into <body> and positioned against the VIEWPORT rather than being an absolutely
// positioned child of the trigger, which any scrolling ancestor would clip — see `useAnchoredPosition`
// (spec-49 DEFERRED §5 FINDING).
const MobileActionsMenu: FC<MobileActionsMenuProps> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const position = useAnchoredPosition(open, rootRef, dropdownRef, "end");
  const { t } = useTranslate();

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      // The dropdown is no longer a descendant of rootRef — without this it would count as "outside" and
      // unmount on the pointerdown that precedes an item's own click.
      if (rootRef.current?.contains(target) || dropdownRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div className={styles.mobileMenuRoot} ref={rootRef}>
      <button
        type="button"
        className={styles.mobileMenuTrigger}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={t`More actions`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={styles.mobileMenuTriggerDot} />
        <span className={styles.mobileMenuTriggerDot} />
        <span className={styles.mobileMenuTriggerDot} />
      </button>
      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            className={styles.mobileMenuDropdown}
            role="menu"
            dir={position?.direction}
            style={anchoredPopoverStyle(position)}
          >
            <div
              className={styles.mobileMenuDropdownInner}
              onClick={() => setOpen(false)}
            >
              {children}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default MobileActionsMenu;
